// PRD-962 (v13) — dataset status badge + hover tooltip.
//
// The backend rolls per-file states up into a finished status model and returns
// `badge`, `queryable`, and `file_counts` per dataset. We RENDER those fields
// verbatim — the badge and queryability are product-policy decisions that live
// server-side so they can't drift. Do NOT recompute them on the client here.
//
// Note token !== label: `preparing` shows as "Processing", `action_needed` as
// "Issues". Map by token; never rename the token.
//
// Three separate vocabularies use review wording and mixing them is a silent
// rendering bug, so keep them straight:
//   badge (here)        -> `review_needed`
//   per-file status     -> `awaiting_review`  (see DatasetDetails STATUS_ICON)
//   file_counts bucket  -> `awaiting_review`
//   job_status          -> unchanged; never carries a review value
//
// `badge === null` (or absent) means an empty dataset (0 files): callers should
// show the onboarding prompt and no chip — getDatasetBadge returns null for it.

const BADGE_DISPLAY = {
  preparing: { label: 'Processing', variant: 'processing' },
  ready: { label: 'Ready', variant: 'success' },
  action_needed: { label: 'Issues', variant: 'alert' },
  // Document field review (HITL): a file is parked awaiting a human. The token is
  // `review_needed` — NOT `awaiting_review`, which is the per-file status and the
  // count bucket. Label matches the wording reviewers already see on the CSV
  // review path so the card reads the same for both flows.
  review_needed: { label: 'Review required', variant: 'alert' },
  failed: { label: 'Failed', variant: 'error' },
};

/**
 * Map the server `badge` token to its label + <Chip> variant (PRD-1361).
 * @returns {{label: string, variant: string} | null} null for empty datasets.
 */
export function getDatasetBadge(dataset) {
  const badge = dataset?.badge;
  if (!badge) return null;
  return BADGE_DISPLAY[badge] || null;
}

// "1 needs attention" vs "2 need attention".
function needVerb(n) {
  return n === 1 ? 'needs' : 'need';
}

/**
 * Build the count line from `file_counts`, per the v13 policy for each badge.
 * Rendered inline under the chip on roomy cards, and as the chip's hover tooltip
 * in the dense sidebar list — same text either way. The unit is always "files".
 * Unlike the badge, a non-empty dataset always gets a line (including a clean
 * `ready` dataset, e.g. "2 of 2 files ready"). Returns null only for an
 * empty/unknown badge.
 */
export function getDatasetCountLine(dataset) {
  const badge = dataset?.badge;
  if (!badge) return null;

  const counts = dataset.file_counts || {};
  const total = counts.total ?? 0;
  const ready = counts.ready ?? 0;
  const processing = counts.processing ?? 0;
  const awaitingReview = counts.awaiting_review ?? 0;
  const needsAttention = counts.needs_attention ?? 0;

  // `awaiting_review` is its own bucket and is deliberately NOT counted inside
  // needs_attention (which stays "failed + dead-lettered"), so the partition
  // now has four terms: ready + processing + awaiting_review + needs_attention
  // === total. Omitting the review term would silently under-report the line.
  // Withheld while anything is still processing: the badge says `preparing` in
  // that state and the Review button is withheld too (see `reviewBatchReady` in
  // DataSources.jsx), so naming review here would advertise work the reviewer
  // cannot yet start. Once the batch settles, badge, count line and button all
  // surface review together.
  // Design Q3: mid-batch the queue is still NAMED — as progress ("queued for review"),
  // not as an invitation ("need review"), since the Review button stays withheld until the
  // batch settles. Without this, a long enrich queue with several files already parked
  // reads as a stalled "Processing", which testers consistently read as breakage.
  const reviewSuffix = awaitingReview > 0
    ? (processing === 0
      ? `, ${awaitingReview} ${needVerb(awaitingReview)} review`
      : `, ${awaitingReview} queued for review`)
    : '';

  switch (badge) {
    case 'preparing': {
      let line = `${ready} of ${total} files ready`;
      // PRD-1618: a partly ready dataset now reads Processing too, so say what's left.
      if (ready > 0 && processing > 0) {
        line += `, ${processing} still processing`;
      }
      if (needsAttention > 0) {
        line += `, ${needsAttention} ${needVerb(needsAttention)} attention`;
      }
      return line + reviewSuffix;
    }
    case 'ready': {
      let line = `${ready} of ${total} files ready`;
      if (processing > 0) {
        line += `, ${processing} still processing`;
      }
      return line + reviewSuffix;
    }
    case 'action_needed': {
      let line = `${ready} of ${total} files ready, ${needsAttention} ${needVerb(needsAttention)} attention`;
      line += reviewSuffix;
      if (processing > 0) {
        line += `, ${processing} processing`;
      }
      return line;
    }
    case 'review_needed':
      // Badge precedence is processing > failed > review, so reaching this case
      // means nothing is in flight and nothing has failed.
      return `${ready} of ${total} files ready${reviewSuffix}`;
    case 'failed':
      // A dataset reaches `failed` when nothing is queryable, and a parked file
      // is not ready and therefore not queryable — so failures and parked files
      // can coexist here. Report the review term or those files vanish from the
      // line entirely.
      return `0 of ${total} files processed${reviewSuffix}`;
    default:
      return null;
  }
}
